/**
 * LoadingManager - Centralized loading progress tracking
 * 
 * Emits events via EventBus (internal) and DOM CustomEvents (for external embedding apps)
 */

import { EventBus } from './EventBus';

export interface LoadingProgress {
  loaded: number;
  total: number;
  percent: number;
  currentItem: string;
}

class LoadingManagerImpl {
  private total = 0;
  private loaded = 0;
  private currentItem = '';
  private eventBus: EventBus | null = null;
  private started = false;
  private completed = false;

  /**
   * Set the EventBus instance for internal event emission
   */
  setEventBus(eventBus: EventBus): void {
    this.eventBus = eventBus;
  }

  /**
   * Set the total number of items to load and start tracking
   */
  setTotal(count: number): void {
    this.total = count;
    this.loaded = 0;
    this.currentItem = '';
    this.started = true;
    this.completed = false;
    this.emit('loading:start', this.getProgress());
  }

  /**
   * Report that an item has been loaded
   */
  reportLoaded(itemName: string): void {
    if (!this.started || this.completed) return;
    
    this.loaded++;
    this.currentItem = itemName;
    
    const progress = this.getProgress();
    this.emit('loading:progress', progress);

    if (this.loaded >= this.total) {
      this.completed = true;
      this.emit('loading:complete', progress);
    }
  }

  /**
   * Get current loading progress
   */
  getProgress(): LoadingProgress {
    const percent = this.total > 0 ? Math.round((this.loaded / this.total) * 100) : 0;
    return {
      loaded: this.loaded,
      total: this.total,
      percent,
      currentItem: this.currentItem,
    };
  }

  /**
   * Check if loading is complete
   */
  isComplete(): boolean {
    return this.completed;
  }

  /**
   * Emit event via EventBus (internal) and DOM CustomEvent (external)
   */
  private emit(event: string, data: LoadingProgress): void {
    // Internal EventBus
    if (this.eventBus) {
      this.eventBus.emit(event, data);
    }

    // DOM CustomEvent for external embedding apps
    const domEventName = event === 'loading:start' ? 'himum-loading-start'
      : event === 'loading:progress' ? 'himum-loading-progress'
      : 'himum-loading-complete';
    
    window.dispatchEvent(new CustomEvent(domEventName, { detail: data }));
  }
}

// Singleton instance
export const loadingManager = new LoadingManagerImpl();
